import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { supabase } from '../../../lib/supabase';

const Login = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [errors, setErrors] = useState<{ email?: string; password?: string }>({});
  const [authError, setAuthError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const validateForm = () => {
    const newErrors: { email?: string; password?: string } = {};

    if (!formData.email) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email';
    }

    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setAuthError(null);

    if (!validateForm()) return;

    setIsSubmitting(true);
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email: formData.email,
        password: formData.password,
      });

      if (error) throw error;

      // Fetch the user's role from profiles
      const { data: profile } = await supabase
        .from('profiles')
        .select('role')
        .eq('id', data.user.id)
        .maybeSingle();

      navigate(profile?.role === 'writer' ? '/profile/writer' : '/profile/reader');
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Invalid email or password.';
      setAuthError(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    // Clear error when user starts typing
    if (errors[name as keyof typeof errors]) {
      setErrors(prev => ({ ...prev, [name]: undefined }));
    }
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center justify-center space-x-3 group">
            <img
              src="https://static.readdy.ai/image/cd80e8551adaa83aa4e45d1e4f56c590/c334bfc7bb42624cba9a352752f4949a.png"
              alt="Sinister Files Logo"
              className="h-16 w-16 object-contain transition-transform duration-300 group-hover:scale-110"
            />
            <span className="text-3xl font-bold text-red-600 font-['Cinzel'] tracking-wider">
              SINISTER FILES
            </span>
          </Link>
        </div>

        {/* Login Card */}
        <div className="bg-gradient-to-br from-[#1a0a0a] to-[#0f0f0f] border border-red-900/20 rounded-lg p-8 shadow-2xl shadow-red-900/20">
          <h1 className="text-3xl font-bold text-white mb-2 font-['Cinzel'] text-center">
            Welcome Back
          </h1>
          <p className="text-gray-400 text-center mb-8 font-['Crimson_Text']">
            Sign in to continue your dark journey
          </p>

          {/* Auth Error Banner */}
          {authError && (
            <div className="mb-6 px-4 py-3 bg-red-900/30 border border-red-600/50 rounded-lg flex items-start gap-2">
              <i className="ri-error-warning-line text-red-400 mt-0.5 shrink-0"></i>
              <p className="text-sm text-red-300 font-['Crimson_Text']">{authError}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Email Field */}
            <div>
              <label htmlFor="email" className="block text-sm font-semibold text-gray-300 mb-2 font-['Crimson_Text']">
                Email Address
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                className={`w-full px-4 py-3 bg-[#0a0a0a] border ${
                  errors.email ? 'border-red-500' : 'border-red-900/30'
                } rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-red-600 transition-colors font-['Crimson_Text'] text-sm`}
                placeholder="your@email.com"
              />
              {errors.email && (
                <p className="mt-2 text-sm text-red-500 font-['Crimson_Text'] flex items-center">
                  <i className="ri-error-warning-line mr-1"></i>
                  {errors.email}
                </p>
              )}
            </div>

            {/* Password Field */}
            <div>
              <label htmlFor="password" className="block text-sm font-semibold text-gray-300 mb-2 font-['Crimson_Text']">
                Password
              </label>
              <input
                type="password"
                id="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                className={`w-full px-4 py-3 bg-[#0a0a0a] border ${
                  errors.password ? 'border-red-500' : 'border-red-900/30'
                } rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-red-600 transition-colors font-['Crimson_Text'] text-sm`}
                placeholder="Enter your password"
              />
              {errors.password && (
                <p className="mt-2 text-sm text-red-500 font-['Crimson_Text'] flex items-center">
                  <i className="ri-error-warning-line mr-1"></i>
                  {errors.password}
                </p>
              )}
            </div>

            {/* Forgot Password Link */}
            <div className="text-right">
              <Link
                to="/forgot-password"
                className="text-sm text-red-500 hover:text-red-400 transition-colors font-['Crimson_Text'] cursor-pointer"
              >
                Forgot password?
              </Link>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full px-6 py-3 bg-red-600 hover:bg-red-700 disabled:bg-red-800 disabled:cursor-not-allowed text-white font-semibold rounded-lg transition-all duration-300 shadow-lg shadow-red-900/30 hover:shadow-red-900/50 font-['Crimson_Text'] whitespace-nowrap cursor-pointer"
            >
              {isSubmitting ? (
                <span className="flex items-center justify-center">
                  <i className="ri-loader-4-line animate-spin mr-2"></i>
                  Signing In...
                </span>
              ) : (
                'Sign In'
              )}
            </button>
          </form>

          {/* Register Link */}
          <div className="mt-8 text-center">
            <p className="text-gray-400 font-['Crimson_Text']">
              Don&apos;t have an account?{' '}
              <Link
                to="/register"
                className="text-red-500 hover:text-red-400 font-semibold transition-colors cursor-pointer"
              >
                Create Account
              </Link>
            </p>
          </div>
        </div>

        {/* Back to Home */}
        <div className="text-center mt-6">
          <Link
            to="/"
            className="text-gray-400 hover:text-red-500 transition-colors font-['Crimson_Text'] text-sm cursor-pointer inline-flex items-center"
          >
            <i className="ri-arrow-left-line mr-2"></i>
            Back to Home
          </Link>
        </div>
      </div>

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        .animate-spin {
          animation: spin 1s linear infinite;
        }
      `}</style>
    </div>
  );
};

export default Login;